import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent
} from '@mui/material';
import {
  CheckCircle,
  Email,
  Login
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../common/Controls';
import { stripeService } from '../../services/payment/stripeService';
import { loadGA, trackEvent } from '../../utils/analytics';
import { themeUtils } from '../../theme';

const SuccessContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: themeUtils.gradients.backgroundLight,
  padding: theme.spacing(3),
}));

const SuccessCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  boxShadow: `0 20px 60px rgba(37, 99, 235, 0.15)`,
  border: `1px solid ${themeUtils.colors.blue['blue-100']}`,
  maxWidth: '500px',
  width: '100%',
  textAlign: 'center',
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const SuccessIcon = styled(CheckCircle)(({ theme }) => ({
  fontSize: '4rem',
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
}));

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const processingRef = useRef(false); // Prevent duplicate processing

  useEffect(() => {
    // GA4: load on this acquisition page (deep-link safe). No-op in dev / when authed.
    loadGA();

    const processPayment = async () => {
      // PRODUCTION DEBUG - Check if updated code is deployed
      console.log('🚀 PRODUCTION DEBUG: Updated PaymentSuccessPage is running!');
      console.log('🚀 Current location:', window.location.href);
      
      // Prevent duplicate processing
      if (processingRef.current) {
        console.log('🚫 Payment processing already in progress, skipping...');
        return;
      }
      
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        setMessage('Invalid payment session. Please try again.');
        return;
      }
      
      processingRef.current = true; // Mark as processing

      try {
        // Verify payment status
        const verificationResult = await stripeService.verifyPayment(sessionId);
        
        console.log('🔍 Payment verification result:', verificationResult);
        console.log('🔍 Verification success:', verificationResult.success);
        console.log('🔍 Verification status:', verificationResult.status);
        console.log('🔍 Status comparison:', verificationResult.status === 'paid');
        console.log('🔍 Full verification object:', JSON.stringify(verificationResult, null, 2));
        
        // More robust status check (handle case sensitivity and type issues)
        const isPaid = verificationResult.success && 
                      verificationResult.status && 
                      verificationResult.status.toString().toLowerCase() === 'paid';
        
        console.log('🔍 Robust status check:', isPaid);
        
        if (isPaid) {
          // GA4 funnel: the TRUE conversion. Fire once; GA dedupes refires by
          // transaction_id (the Stripe session id). Plan/value come from the blob
          // stashed at checkout; fall back to currency-only on a deep-link/refresh.
          let purchaseParams = { transaction_id: sessionId, currency: 'AED' };
          try {
            const checkout = JSON.parse(sessionStorage.getItem('signupCheckout') || '{}');
            if (checkout.value != null) purchaseParams.value = checkout.value;
            if (checkout.currency) purchaseParams.currency = checkout.currency;
            if (checkout.planName) purchaseParams.plan = checkout.planName;
          } catch (e) { /* ignore malformed blob */ }
          trackEvent('purchase', purchaseParams);
          sessionStorage.removeItem('signupCheckout');

          // Payment successful, complete signup
          const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
          
          console.log('🔍 Signup data from session:', signupData);
          
          if (Object.keys(signupData).length === 0) {
            setStatus('error');
            setMessage('Signup data not found. Please try signing up again.');
            return;
          }

          console.log('🔄 Attempting to complete signup with sessionId:', sessionId);
          const signupResult = await stripeService.completeSignupAfterPayment(sessionId, signupData);
          console.log('🔍 Signup completion result:', signupResult);
          
          if (signupResult.success) {
            setStatus('success');
            setUserEmail(signupData.username);
            setMessage('Your account has been created successfully! Please check your email for verification.');
            
            // Clear stored signup data
            sessionStorage.removeItem('signupData');
          } else {
            // Check if it's a "user already exists" error - treat as success
            const errorMessage = typeof signupResult.message === 'string' 
              ? signupResult.message 
              : 'Failed to complete account creation.';
            
            if (errorMessage.toLowerCase().includes('already exists') || 
                errorMessage.toLowerCase().includes('username already') ||
                errorMessage.toLowerCase().includes('email already')) {
              console.log('👤 User already exists - treating as success since payment was completed');
              setStatus('success');
              setUserEmail(signupData.username);
              setMessage('Your account has been created successfully! Please check your email for verification.');
              sessionStorage.removeItem('signupData');
            } else {
              setStatus('error');
              setMessage(errorMessage);
            }
          }
        } else {
          setStatus('error');
          setMessage('Payment verification failed. Please contact support.');
        }
      } catch (error) {
        console.error('Payment processing error:', error);
        setStatus('error');
        // Ensure error message is always a string
        const errorMessage = typeof error === 'object' && error.message 
          ? String(error.message)
          : 'An error occurred while processing your payment. Please contact support.';
        setMessage(errorMessage);
      } finally {
        processingRef.current = false; // Reset processing flag
      }
    };

    processPayment();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (status === 'processing') {
    return (
      <SuccessContainer>
        <SuccessCard>
          <LoadingBox>
            <CircularProgress size={60} />
            <Typography variant="h5" fontWeight={600}>
              Processing Your Payment...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your payment and create your account.
            </Typography>
          </LoadingBox>
        </SuccessCard>
      </SuccessContainer>
    );
  }

  if (status === 'error') {
    return (
      <SuccessContainer>
        <SuccessCard>
          <Stack spacing={3} alignItems="center">
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%',
                borderRadius: 2,
                fontSize: '1rem'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Payment Error
              </Typography>
              {message}
            </Alert>
            
            <EasyCheckButton
              variant="contained"
              onClick={() => navigate('/signup')}
              sx={{
                background: themeUtils.gradients.primary,
                '&:hover': {
                  background: themeUtils.gradients.primaryDark,
                },
              }}
            >
              Try Again
            </EasyCheckButton>
          </Stack>
        </SuccessCard>
      </SuccessContainer>
    );
  }

  return (
    <SuccessContainer>
      <SuccessCard>
        <Stack spacing={3} alignItems="center">
          <SuccessIcon />
          
          <Typography variant="h4" fontWeight={700} color="success.main">
            Payment Successful!
          </Typography>
          
          <Typography variant="h6" fontWeight={600}>
            Welcome to EasyCheck
          </Typography>

          <Alert 
            severity="success" 
            icon={<Email />}
            sx={{ 
              width: '100%',
              borderRadius: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              border: '1px solid rgba(76, 175, 80, 0.2)',
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Account Created Successfully!
            </Typography>
            <Typography variant="body2">
              We've sent a verification email to <strong>{userEmail}</strong>. 
              Please check your email and click the verification link to activate your account.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Your payment of $99.00 has been processed successfully. 
            You can now access all EasyCheck features once your email is verified.
          </Typography>

          <EasyCheckButton
            variant="contained"
            size="large"
            startIcon={<Login />}
            onClick={handleGoToLogin}
            sx={{
              background: themeUtils.gradients.primary,
              '&:hover': {
                background: themeUtils.gradients.primaryDark,
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
              },
              padding: '12px 32px',
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            Go to Login
          </EasyCheckButton>

          <Typography variant="caption" color="text.secondary">
            Didn't receive the email? Check your spam folder or contact support.
          </Typography>
        </Stack>
      </SuccessCard>
    </SuccessContainer>
  );
};

export default PaymentSuccessPage; 